import { useEffect, useRef } from "react";
import { usePulz } from "@/api/hooks/usePraca";

/** Ako často posielať pulz, kým je zákazka otvorená a človek pri nej. */
export const PULZ_INTERVAL_MS = 60_000;

/**
 * Nečinnosť, po ktorej sa pulz prestane posielať.
 *
 * Backend reláciu uzavrie po 5 minútach bez pulzu, takže tento prah
 * musí byť **kratší**: inak by sa naposledy poslal pulz tesne pred
 * uzavretím a do práce by sa započítala aj pauza pred ním.
 */
export const NEAKTIVITA_MS = 3 * 60_000;

/**
 * Meranie času na zákazke — pulz kým je editor otvorený a človek pri ňom.
 *
 * Aktivitou je pohyb myši, klávesa, klik, scroll a návrat na kartu. Bez
 * tejto podmienky by otvorená karta cez obed alebo cez noc narástla na
 * hodiny „práce" — presne to, čo meranie nemá robiť.
 *
 * Pulz sa neposiela, kým je karta skrytá (`document.hidden`) — otvorený
 * tab na druhej obrazovke tiež nie je práca.
 *
 * Prvý pulz ide **hneď** pri otvorení, nie až po minúte: relácia sa tým
 * začne v čase otvorenia a krátka návšteva sa v histórii aspoň objaví
 * (s trvaním 0).
 */
export function useHeartbeat(zakazkaId: string | undefined): void {
  const pulz = usePulz();
  const poslednaAktivita = useRef(Date.now());
  // Mutácia sa pri každom rendere mení — bez ref by sa interval
  // preplánovával a nikdy nedobehol.
  const posli = useRef<(id: string) => void>(() => {});

  posli.current = (id: string) => {
    if (!pulz.isPending) pulz.mutate(id);
  };

  useEffect(() => {
    if (!zakazkaId) return;

    const zaznamenajAktivitu = (): void => {
      poslednaAktivita.current = Date.now();
    };
    const udalosti = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
    ] as const;
    for (const u of udalosti) {
      window.addEventListener(u, zaznamenajAktivitu, { passive: true });
    }
    document.addEventListener("visibilitychange", zaznamenajAktivitu);

    posli.current(zakazkaId);
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      if (Date.now() - poslednaAktivita.current > NEAKTIVITA_MS) return;
      posli.current(zakazkaId);
    }, PULZ_INTERVAL_MS);

    return () => {
      window.clearInterval(timer);
      for (const u of udalosti) {
        window.removeEventListener(u, zaznamenajAktivitu);
      }
      document.removeEventListener("visibilitychange", zaznamenajAktivitu);
    };
    // Zámerne len `zakazkaId` — `posli` je ref, takže sa interval
    // neprerušuje pri každom rendere editora.
  }, [zakazkaId]);
}

/** `4530` → `1 h 15 min`. Sekundy sa ukazujú len pod minútu. */
export function formatTrvanie(sekundy: number): string {
  if (sekundy < 60) return `${sekundy} s`;
  const minuty = Math.floor(sekundy / 60);
  if (minuty < 60) return `${minuty} min`;
  const hodiny = Math.floor(minuty / 60);
  const zbytok = minuty % 60;
  return zbytok === 0 ? `${hodiny} h` : `${hodiny} h ${zbytok} min`;
}
